import {
  Array,
  Object,
  String,
  Number,
  Static,
  InstanceOf,
  Boolean,
  Null,
  Runtype,
  Undefined,
  Unknown,
} from 'runtypes';
import { ContractRecord } from './accountManagement';
import { Route } from './utils';

const DateType = InstanceOf(Date);

export const classRoutes = (): { [name: string]: Route } => ({
  getClass: {
    method: 'get',
    path: '/class/:classId',
    requestBody: Undefined,
    responseData: ClassRecord(DateType),
  },
  getClasses: {
    method: 'get',
    path: '/classes',
    requestBody: Undefined,
    responseData: GetClassesResponseRecord(DateType),
  },
  createClass: {
    method: 'post',
    path: '/class',
    requestBody: PostClassRequestRecord,
    responseData: ClassRecord(DateType),
  },
  editClass: {
    method: 'put',
    path: '/class',
    requestBody: EditClassRequestRecord,
    responseData: ClassRecord(DateType),
  },
  editClassNickName: {
    method: 'put',
    path: '/class/:classId/nickname',
    requestBody: EditClassNickNameRequestRecord,
    responseData: EditClassNickNameResponseRecord,
  },
  removeClass: {
    method: 'delete',
    path: '/class/:classId/remove',
    requestBody: Undefined,
    responseData: RemoveClassResponseRecord,
  },
  /*
   * Licenses are associated with a contract but,
   * in order to handle permissions properly, the call
   * is made with respect to a class.  If the contractId is
   * used, it should be verified.
   */
  updateLicenseStatus: {
    method: 'patch',
    path: '/class/:classId/user/:userId/license',
    requestBody: UpdateUserLicenseRequestRecord,
    responseData: UpdateUserLicenseResponseRecord,
  },
  getContracts: {
    method: 'get',
    path: '/class/:classId/contracts',
    requestBody: Undefined,
    responseData: ClassContractsRecord(DateType),
  },
  getClassLearnerTypes: {
    method: 'get',
    path: '/class/:classId/learner-types',
    requestBody: Undefined,
    responseData: GetLearnerTypesResponseRecord,
  },
  updateLearnerType: {
    method: 'patch',
    path: '/class/:classId/user/:userId/learner-type',
    requestBody: updateLearnerTypeRequestRecord,
    responseData: updateLearnerTypeResponseRecord,
  },
  pushOrInsertClassState: {
    method: 'patch',
    path: '/class/:classId/state',
    requestBody: PushOrInsertClassStateRequestRecord,
    responseData: ClassStateResponseRecord(DateType),
  },
  getClassState: {
    method: 'get',
    path: '/class/:classId/state',
    requestBody: Undefined,
    responseData: ClassStateResponseRecord(DateType),
  },
  getQuizScore: {
    method: 'get',
    path: '/class/:classId/quiz-score',
    requestBody: Undefined,
    responseData: String,
  },
});

const TeacherRecord = <T extends Runtype>(useDate: T) =>
  Object({
    id: Number,
    name: String.or(Null),
    firstName: String.or(Null),
    middleName: String.or(Null),
    lastName: String.or(Null),
    email: String,
    key: String,
    createdAt: useDate,
  });

export const StudentRecord = <T extends Runtype>(useDate: T) =>
  TeacherRecord(useDate).extend({
    licenseStatus: String.or(Null),
    contractId: Number.or(Null),
  });

const NewStudentRecord = Object({
  firstName: String.or(Null),
  middleName: String.or(Null),
  lastName: String.or(Null),
  email: String,
});

export const ClassRecord = <T extends Runtype>(useDate: T) =>
  Object({
    id: Number,
    externalName: String,
    institutionId: Number.or(Null),
    classroomId: Number.or(Null),
    createdAt: useDate,
    updatedAt: useDate.or(Null),
    students: Array(StudentRecord(useDate)),
    teachers: Array(TeacherRecord(useDate)),
    key: String.or(Null),
    externalSource: String.or(Null),
    externalInstance: String.or(Null),
    externalId: String.or(Null),
    startAt: useDate.or(Null),
    endAt: useDate.or(Null),
    nickName: String.or(Null).optional(),
    section: String.or(Null).optional(),
    term: String.or(Null).optional(),
  });

export const GetClassesResponseRecord = <T extends Runtype>(useDate: T) =>
  Object({
    classes: Array(ClassRecord(useDate)),
  });
export const PostClassRequestRecord = Object({
  externalName: String,
  nickName: String.or(Null),
  section: String.or(Null),
  institutionId: Number.or(Null),
  students: Array(NewStudentRecord),
});
export const EditClassRequestRecord = Object({
  id: Number,
  externalName: String,
  nickName: String.or(Null),
  section: String.or(Null),
  institutionId: Number.or(Null),
  students: Array(NewStudentRecord.or(StudentRecord(String))),
});
export const EditClassNickNameRequestRecord = Object({
  id: Number,
  nickName: String.or(Null),
});
export const EditClassNickNameResponseRecord = Object({
  id: Number,
  nickName: String.or(Null),
});

export const UpdateUserLicenseRequestRecord = Object({
  licenseStatus: String,
  contractId: Number,
});

export const GetLearnerTypesResponseRecord = Array(
  Object({
    classId: Number,
    userId: Number,
    firstLearnerType: String,
    secondLearnerType: String,
  }),
);

export const updateLearnerTypeRequestRecord = Object({
  firstLearnerType: String,
  secondLearnerType: String,
});

export const updateLearnerTypeResponseRecord = Object({
  classId: Number,
  userId: Number,
  firstLearnerType: String,
  secondLearnerType: String,
});

// Omit the value to represent deleting a value,
// rather than an explicit "undefined", for
// JSON compatibility.
export const StateRecord = Object({
  name: String,
  value: Unknown.optional(),
});

const UserStateRecord = StateRecord.extend({ userId: Number.or(Null) });

export const ClassUserStateDetailRecord = <T extends Runtype>(useDate: T) =>
  StateRecord.extend({
    userId: Number.or(Null),
    createdAt: useDate,
  });

export const PushOrInsertClassStateRequestRecord = Object({
  push: Boolean,
  undoOverrides: Boolean,
  data: Array(UserStateRecord),
  save: Boolean.optional(),
});

export const ClassStateResponseRecord = <T extends Runtype>(useDate: T) =>
  Object({
    classId: Number,
    userStates: Array(ClassUserStateDetailRecord(useDate)),
  });
export const RemoveClassResponseRecord = Undefined.or(Null);
export const ClassContractsRecord = <T extends Runtype>(useDate: T) =>
  Object({
    classId: Number,
    contracts: Array(ContractRecord(useDate)),
  });
export const UpdateUserLicenseResponseRecord = Undefined.or(Null);

export type NewStudent = Static<typeof NewStudentRecord>;
export const StudentRS = StudentRecord(String);
export type Student = Static<typeof StudentRS>;
const xx = StudentRecord(DateType);
export type StudentDate = Static<typeof xx>;
export const TeacherRS = TeacherRecord(String);
export type Teacher = Static<typeof TeacherRS>;
export const ClassRS = ClassRecord(String);
export type Class = Static<typeof ClassRS>;
const ClassDate = ClassRecord(DateType);
export type ClassWithStudents = Static<typeof ClassDate>;
export const GetClassesResponseRS = GetClassesResponseRecord(String);
export type GetClassesResponse = Static<typeof GetClassesResponseRS>;
export type PostClassRequest = Static<typeof PostClassRequestRecord>;
export type EditClassRequest = Static<typeof EditClassRequestRecord>;
export type EditClassNickNameRequest = Static<typeof EditClassNickNameRequestRecord>;
export type EditClassNickNameResponse = Static<typeof EditClassNickNameResponseRecord>;
export const ClassContractsRS = ClassContractsRecord(String);
export type ClassContracts = Static<typeof ClassContractsRS>;
export type UpdateUserLicenseRequest = Static<typeof UpdateUserLicenseRequestRecord>;
export type UpdateUserLicenseResponse = Static<typeof UpdateUserLicenseResponseRecord>;
export type GetLearnerTypesResponse = Static<typeof GetLearnerTypesResponseRecord>;
export type updateLearnerTypeRequest = Static<typeof updateLearnerTypeRequestRecord>;
export type updateLearnerTypeResponse = Static<typeof updateLearnerTypeResponseRecord>;
export type State = Static<typeof StateRecord>;
export type UserState = Static<typeof UserStateRecord>;
export const ClassUserStateDetailRS = ClassUserStateDetailRecord(String);
export type ClassUserStateDetail = Static<typeof ClassUserStateDetailRS>;
const zz = ClassUserStateDetailRecord(DateType);
export type ClassUserStateDetailDate = Static<typeof zz>;
export const ClassStateResponseRS = ClassStateResponseRecord(String);
export type ClassStateResponse = Static<typeof ClassStateResponseRS>;
export type SetClassState = Static<typeof PushOrInsertClassStateRequestRecord>;
